// ===== authStore.ts — Module-level auth state with pub/sub =====
//
// Tracks the current authenticated user via PocketBase Google OAuth2.
// All other stores read getCurrentUserId() to scope their data.
//
// On Capacitor, the native path keeps PocketBase's supported one-off OAuth
// subscription and only replaces the popup with a system browser tab. This
// preserves the registered callback and lets PocketBase return the session.

import { Capacitor } from '@capacitor/core';
import pb from '../lib/pocketbase';
import type { RecordModel } from 'pocketbase';

export interface PPSUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
}

type AuthListener = () => void;

let currentUser: PPSUser | null = null;
let initialized = false;
const listeners = new Set<AuthListener>();

function notify() {
  listeners.forEach((fn) => fn());
}

function extractUser(record: RecordModel | null): PPSUser | null {
  if (!record) return null;
  return {
    id: record.id,
    email: record.email || '',
    name: record.name || record.email?.split('@')[0] || 'User',
    avatar: record.avatar || undefined,
  };
}

/** Initialize auth from PocketBase persisted authStore (localStorage). Call once on boot. */
export function initAuth(): void {
  if (initialized) return;
  initialized = true;
  currentUser = extractUser(pb.authStore.record as RecordModel | null);

  // Listen for auth changes (logout, token refresh, OAuth return via SSE)
  pb.authStore.onChange(() => {
    currentUser = extractUser(pb.authStore.record as RecordModel | null);
    notify();
  });

  // Keep the session alive (Janna, Oct 2026: "signs out every time I load
  // it"): the token was never refreshed, so it simply expired a few days
  // after sign-in. Refresh on open and whenever the app comes back.
  void refreshSession();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void refreshSession();
    });
  }
}

/** Don't refresh more often than this on resume. */
const REFRESH_MIN_INTERVAL_MS = 60 * 60 * 1000;
let lastRefresh = 0;
let refreshing: Promise<void> | null = null;

/**
 * Swap a still-valid token for a fresh one. Only a definite rejection (401/
 * 403/404: token revoked or user gone) signs out; offline or server trouble
 * keeps the current session untouched.
 */
export function refreshSession(force = false): Promise<void> {
  if (!pb.authStore.isValid) return Promise.resolve();
  if (refreshing) return refreshing;
  if (!force && Date.now() - lastRefresh < REFRESH_MIN_INTERVAL_MS) return Promise.resolve();
  lastRefresh = Date.now();
  refreshing = pb
    .collection('users')
    .authRefresh({ requestKey: 'pps-auth-refresh' })
    .then(() => undefined)
    .catch((err: { status?: number; isAbort?: boolean }) => {
      if (err?.isAbort) return;
      if (err?.status === 401 || err?.status === 403 || err?.status === 404) {
        pb.authStore.clear();
      } else {
        lastRefresh = 0; // offline / server trouble: try again next time
      }
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/** Subscribe to auth state changes. Returns unsubscribe fn. */
export function subscribeAuth(fn: AuthListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Current authenticated user, or null. */
export function getCurrentUser(): PPSUser | null {
  return currentUser;
}

/** Current user ID, or null if not authenticated. */
export function getCurrentUserId(): string | null {
  return currentUser?.id ?? null;
}

/** Whether a user is currently authenticated. */
export function isAuthenticated(): boolean {
  return pb.authStore.isValid && currentUser !== null;
}

// ─── Google OAuth2 login ───────────────────────────────────────────

/**
 * Initiate Google OAuth2 login via PocketBase.
 *
 * Builds the Google auth URL using PKCE params from PocketBase, opens it
 * in Chrome Custom Tabs, and waits for PB's realtime SSE to deliver the
 * auth event when the user returns to the app.
 *
 * Falls back to polling pb.authStore.isValid after the Custom Tab closes.
 */
export async function loginWithGoogle(): Promise<void> {
  // ── Web browser path: use PocketBase's built-in OAuth2 redirect flow. ──
  // PB constructs the redirect_uri from the current request origin automatically,
  // so it always matches what's registered in Google Cloud Console regardless
  // of which domain serves the app (okelo.app/pps/, pps.nip.io, etc.).
  if (!Capacitor.isNativePlatform()) {
    await pb.collection('users').authWithOAuth2({ provider: 'google' });
    // authWithOAuth2 redirects away — on return, PB stores the token and
    // the onChange listener fires. We don't reach here on success.
    currentUser = extractUser(pb.authStore.record as RecordModel | null);
    notify();
    return;
  }

  // ── Capacitor native path: keep PocketBase's supported one-off realtime
  // flow, but open the provider page in a Chrome Custom Tab instead of a
  // WebView popup. PocketBase builds the registered /api/oauth2-redirect URL,
  // receives the code, exchanges it, and saves the auth token.
  const { Browser } = await import('@capacitor/browser');
  const requestKey = 'native-google-oauth';
  const browserHandle = await Browser.addListener('browserFinished', () => {
    if (!pb.authStore.isValid) pb.cancelRequest(requestKey);
  });

  try {
    const authData = await pb.collection('users').authWithOAuth2({
      provider: 'google',
      requestKey,
      urlCallback: (url) => Browser.open({ url }),
    });
    currentUser = extractUser(authData.record as RecordModel);
    notify();
  } finally {
    await browserHandle.remove();
    await Browser.close().catch(() => undefined);
  }
}

/** Logout and clear auth state. Does NOT clear local data (data stays per-user). */
export function logout(): void {
  pb.authStore.clear();
  currentUser = null;
  notify();
}
